// src/components/widget/windows/Window.tsx

import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './window.module.scss';
import Trigger from '../actuators/Trigger';
import Field from '../inputs/Field';

export interface WindowProps {
  message                  : JSX.Element;
  messageType?             : 'information' | 'question';
  messageImportance?       : 'normal' | 'important';
  withField?               : boolean;
  onClickOnValidateButton? : (value?: string) => void;
  onClickOnCancelButton?   : () => void;
  // Bouton secondaire optionnel, affiché complètement à gauche sur la même ligne
  // que Valider / Annuler (ex : « Acheter plus d'espace »).
  onClickOnExtraButton?    : () => void;
  extraButtonText?         : string;
  validButtonText?         : string;
  cancelButtonText?        : string;
  isModal?                 : boolean;
  zIndex?                  : number;
  width?                   : string;
}

// Éléments que Tab peut atteindre.
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Fenêtres modales ouvertes, la plus récente en dernier : quand une fenêtre en ouvre une
// autre par-dessus, seule la dernière retient Tab.
const modalStack: HTMLElement[] = [];

const ACCEPT_BUTTON_COLORS = {
  buttonBackgroundColor      : 'rgb(175, 202, 247)',
  buttonBackgroundHoverColor : 'rgb(150, 184, 243)',
  buttonForegroundColor      : '#0f172a',
  buttonForegroundHoverColor : '#0f172a',
} as const;

export const Window = ({ message                                                                    ,
                         messageType             = 'information'                                    ,
                         messageImportance       = 'normal'                                         ,
                         withField               = false                                            ,
                         onClickOnValidateButton                                                    ,
                         onClickOnCancelButton                                                      ,
                         onClickOnExtraButton                                                       ,
                         extraButtonText                                                            ,
                         validButtonText         = messageType === 'information' ? 'Ok' : 'Valider' ,
                         cancelButtonText        = 'Annuler'                                        ,
                         isModal                 = true                                             ,
                         zIndex                  = 600                                              ,
                         width                                                                       }: WindowProps) => {

  const [ fieldValue , setFieldValue ] = useState('');

  const windowRef = useRef<HTMLDivElement>(null);
  // Lu au premier rendu, donc AVANT que l'autoFocus d'un bouton de la fenêtre ne déplace le
  // focus : c'est l'élément qui l'a ouverte, celui à qui on le rend à la fermeture.
  const previouslyFocusedRef = useRef<HTMLElement | null>(
    typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null
  );

  // Fenêtre modale : Tab reste dans la fenêtre. Sans ça, le focus filait sur les éléments
  // de la page cachée derrière l'overlay, où Entrée aurait agi à l'aveugle.
  useEffect(() => {
    if (!isModal) return;
    const node = windowRef.current;
    if (!node) return;

    const focusables = () => Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE))
      .filter(el => el.getClientRects().length > 0);

    modalStack.push(node);
    if (!node.contains(document.activeElement)) (focusables()[0] ?? node).focus();

    const onKeyDown = (e: KeyboardEvent) => {
      const isTab   = e.key === 'Tab';
      const isArrow = e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown';
      if (!isTab && !isArrow) return;
      if (modalStack[modalStack.length - 1] !== node) return;

      const active = document.activeElement as HTMLElement | null;

      // Les flèches font comme Tab / Maj+Tab, sauf là où elles ont déjà un rôle : dans un champ
      // de saisie (curseur, liste déroulante) ou avec un modificateur (Alt+← = page précédente).
      if (isArrow) {
        if (e.ctrlKey || e.altKey || e.metaKey || e.shiftKey) return;
        if (active && (active.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName))) return;
      }

      const backwards = isTab ? e.shiftKey : (e.key === 'ArrowLeft' || e.key === 'ArrowUp');

      const items = focusables();
      if (items.length === 0) { e.preventDefault(); node.focus(); return; }

      const first = items[0];
      const last  = items[items.length - 1];

      // Focus hors de la fenêtre (clic sur l'overlay, par exemple) : on y rentre.
      if (!active || !node.contains(active)) { e.preventDefault(); (backwards ? last : first).focus(); return; }

      if (isArrow) {
        // Pas de comportement natif à laisser faire : on avance nous-mêmes, en boucle comme Tab.
        e.preventDefault();
        const i = items.indexOf(active);
        items[i === -1 ? 0 : (i + (backwards ? -1 : 1) + items.length) % items.length].focus();
        return;
      }

      if (backwards && active === first)        { e.preventDefault(); last.focus();  }
      else if (!backwards && active === last)   { e.preventDefault(); first.focus(); }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      const i = modalStack.lastIndexOf(node);
      if (i !== -1) modalStack.splice(i, 1);
      const back = previouslyFocusedRef.current;
      if (back && back.isConnected && (node.contains(document.activeElement) || document.activeElement === document.body))
        back.focus();
    };
  }, [isModal]);

  const handleValidateButtonClick = () => {
    
	if (withField) 
	      {
           onClickOnValidateButton?.(fieldValue);
          } 
     else {
           onClickOnValidateButton?.();
          }
  };

  const handleCancelButtonClick = () => {
    onClickOnCancelButton?.();
  };

  const generateClassName = (): string => {
    return messageImportance === 'important' ? styles.importantMessage : styles.normalMessage;
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(e.target.value);
  };

  return  (
            <>
              { isModal && <div className={styles.overlay} style={{ zIndex: zIndex - 1 }} /> }
              <div ref={windowRef} tabIndex={-1} className={styles.window} style={{ zIndex: zIndex, ...(width ? { width } : {}) }}>
                
				<p className = {classNames(styles.message, generateClassName())}>
				  {message}
				</p>

                { withField && <Field value    = {fieldValue} 
				                      onChange = {handleFieldChange} /> }

                <div className = {styles.buttonContainer}>

		          {
				   onClickOnExtraButton &&
				   extraButtonText &&
					(
                     <div className = {styles.extraButton}>
                       <Trigger name    = {extraButtonText}
					            onClick = {onClickOnExtraButton}
								variant = "tailwind2" />
                     </div>
                    )
				  }

		          {
				   messageType === 'question' && 
				   onClickOnCancelButton && 
					(
                     <Trigger name      = {cancelButtonText} 
					          onClick   = {handleCancelButtonClick} 
					          variant   = "tailwind2"
					          autoFocus = {!withField} />
                    )
			  	  }

                  {
				   onClickOnValidateButton && 
				    (
                     <Trigger name      = {validButtonText} 
				              onClick   = {handleValidateButtonClick} 
							  variant   = "tailwind2"
							  // Sur une question, c'est le bouton d'acceptation : il porte la couleur
							  // d'accord. Le focus initial va à « Annuler » (plus sûr pour une action
							  // irréversible) ; sans bouton d'annulation, ou en simple information,
							  // il revient à celui-ci.
							  autoFocus = {!withField && !(messageType === 'question' && onClickOnCancelButton)}
							  {...(messageType === 'question' ? ACCEPT_BUTTON_COLORS : {})} />
                    )
				  }
                
				</div>
      
	          </div>
			  
            </>
			
          );
};

export default Window;